"use client"

import type { Dispatch, SetStateAction } from "react"
import type { BooruPost } from "@/lib/booru-api"
import type { UserScript, CommunityScript } from "@/lib/script-store"
import { useScriptLinkingStore } from "@/lib/script-linking-store"

// Moved out of components/booru-detail-modal.tsx (same functions, same behaviour).
const BOORU_SCRIPT_PLAY_OPTIONS = { skipHSSP: false } as const
const BOORU_SCRIPT_SET_OPTIONS = { skipPrewarm: true } as const

export interface ModalScriptActionsInput {
  post: BooruPost | null
  isAlbum: boolean
  currentAlbumImageUrl: string | null
  isMediaVideo: boolean
  activeVideoUrl: string | null
  effectiveAlbumImages: string[]
  setShowInfo: Dispatch<SetStateAction<boolean>>
  setShowComments: Dispatch<SetStateAction<boolean>>
  setShowReportDialog: Dispatch<SetStateAction<boolean>>
  setInlineMarketScript: Dispatch<SetStateAction<CommunityScript | null>>
  onOpenChange: (open: boolean) => void
  onOpenEditors: (() => void) | undefined
  onAddToBounty: ((post: BooruPost) => void) | undefined
  onAddToCommission: ((post: BooruPost) => void) | undefined
  selectPostForScript: (post: BooruPost, preferredTab?: "curve" | "block" | "manual") => void
  openPip: (post: BooruPost) => void
  setActiveScript: Dispatch<SetStateAction<UserScript | CommunityScript | null>>
  setShowScriptPlayer: Dispatch<SetStateAction<boolean>>
  setGlobalScript: (script: UserScript | CommunityScript, opts?: { skipPrewarm?: boolean }) => void
  globalPlay: (opts?: { skipHSSP?: boolean }) => Promise<void>
  isDeviceConnected: boolean
  setShowQuickConnect: Dispatch<SetStateAction<boolean>>
  setShowDeviceXYPad: Dispatch<SetStateAction<boolean>>
}

export function useModalScriptActions({
  post, isAlbum, currentAlbumImageUrl, isMediaVideo, activeVideoUrl, effectiveAlbumImages,
  setShowInfo, setShowComments, setShowReportDialog, setInlineMarketScript, onOpenChange, onOpenEditors,
  onAddToBounty, onAddToCommission, selectPostForScript, openPip, setActiveScript, setShowScriptPlayer,
  setGlobalScript, globalPlay, isDeviceConnected, setShowQuickConnect, setShowDeviceXYPad,
}: ModalScriptActionsInput) {
  // Enrich post with resolved album pages before handing to any editor/player.
  // For doujins the pages are fetched lazily — effectiveAlbumImages has them.
  const getEnrichedPost = () => {
    if (!post) return null
    const visibleMediaUrl =
      isAlbum && currentAlbumImageUrl
        ? currentAlbumImageUrl
        : isMediaVideo && activeVideoUrl
          ? activeVideoUrl
          : undefined

    const enriched = {
      ...post,
      ...(effectiveAlbumImages && effectiveAlbumImages.length > 0 ? { albumImages: effectiveAlbumImages } : {}),
      ...(visibleMediaUrl ? { fileUrl: visibleMediaUrl, sampleUrl: visibleMediaUrl } : {}),
    }

    return enriched
  }

  const closeModalSurfaces = () => {
    setShowInfo(false)
    setShowComments(false)
    setShowReportDialog(false)
    setInlineMarketScript(null)
    onOpenChange?.(false)
  }

  const openEditorsAfterSelection = () => {
    onOpenEditors?.()
  }

  const handleAddToScript = () => {
    const enriched = getEnrichedPost()
    // Gooncave owns its own return routing (app/page.tsx switches to the gooncave view and
    // Gooncave drops the post into the slot that opened the picker). Sending it through
    // openEditorsAfterSelection would land the user in the Editors Hub instead.
    const toGooncave = useScriptLinkingStore.getState().returnToTab === "Gooncave"
    closeModalSurfaces()
    if (enriched) {
      selectPostForScript(enriched)
      if (!toGooncave) openEditorsAfterSelection()
    }
  }

  const handleAddToVerticalEditor = () => {
    const enriched = getEnrichedPost()
    closeModalSurfaces()
    if (enriched) {
      useScriptLinkingStore.setState({ returnToTab: "vertical-editor" })
      selectPostForScript(enriched)
      openEditorsAfterSelection()
    }
  }

  const handleOpenPip = () => {
    if (post) {
      openPip(activeVideoUrl ? { ...post, fileUrl: activeVideoUrl, type: "video" } : post)
      onOpenChange(false) // Close the modal when opening PIP
    }
  }

  const handlePlayScript = (script: UserScript | CommunityScript) => {
    setActiveScript(script)
    setShowScriptPlayer(true)
    setGlobalScript(script, BOORU_SCRIPT_SET_OPTIONS)
    globalPlay(BOORU_SCRIPT_PLAY_OPTIONS).catch(() => {})
  }

  const handleCreateScript = () => {
    const enriched = getEnrichedPost()
    // Always close the modal first — the editor will open via the linking store
    closeModalSurfaces()
    if (enriched) {
      selectPostForScript(enriched)
      openEditorsAfterSelection()
    }
  }

  const handleManualControl = () => {
    if (!isDeviceConnected) {
      setShowQuickConnect(true)
      return
    }
    setShowDeviceXYPad((v) => !v)
  }

  const handleRequestBounty = () => {
    const enriched = getEnrichedPost()
    if (enriched) {
      useScriptLinkingStore.setState({
        returnToTab: "bounties",
        pendingPost: enriched,
        returnToEditor: true,
        isLinkingMode: false,
      })
      closeModalSurfaces()
    }
  }

  const handleAddToBountySelection = () => {
    const enriched = getEnrichedPost() || post
    if (!enriched || !onAddToBounty) return
    onAddToBounty(enriched)
  }

  const handleAddToCommissionSelection = () => {
    const enriched = getEnrichedPost() || post
    if (!enriched || !onAddToCommission) return
    onAddToCommission(enriched)
  }

  return {
    getEnrichedPost, closeModalSurfaces, openEditorsAfterSelection, handleAddToScript, handleAddToVerticalEditor, handleOpenPip, handlePlayScript, handleCreateScript,
    handleManualControl, handleRequestBounty, handleAddToBountySelection, handleAddToCommissionSelection,
  }
}
